// src/App.tsx
import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import store from "./store";
import theme from "./theme";
import { RTL } from "./theme/rtl";
import MainScreen from "./screens/MainScreen";
import CustomerMainScreen from "./screens/CustomerMainScreen";
import PhoneInputScreen from "./screens/PhoneInputScreen";
import CustomerVerifyOtpScreen from "./screens/OtpInputScreen";
import CustomerServicesScreen from "./screens/CustomerServicesScreen";
import CustomerExistingSlotsScreen from "./screens/CustomerExistingSlotsScreen";
import CustomerSlotsScreen from "./screens/CustomerSlotsScreen";
import CustomerSuccessScreen from "./screens/CustomerSuccessScreen";
import DashboardScreen from "./screens/DashboardScreen";
import OwnerInfoScreen from "./screens/Onboarding/OwnerInfoScreen";
import ServicesSetupScreen from "./screens/ServicesSetupScreen";
import AuthorizeScreen from "./screens/Onboarding/AuthorizeScreen";
import SlotsSetupScreen from "./screens/SlotsSetupScreen";
import OwnerSuccessScreen from "./screens/OwnerSuccessScreen";
import AvailabilityScreen from "./screens/AvailabilityScreen";
import PrivacyPolicyScreen from "./screens/Legal/PrivacyPolicyScreen";
import TermsOfServiceScreen from "./screens/Legal/TermsOfServiceScreen";
import NotAuthorizedScreen from "./screens/NotAuthorized";
import ShortLinkRedirectScreen from "./screens/ShortLinkRedirectionScreen";
import InsightsScreen from "./screens/InsightsScreen";
import ConfirmReminderScreen from "./screens/Reminders/ConfirmScreen";
import CancelReminderScreen from "./screens/Reminders/CancelScreen";
import RescheduleReminderScreen from "./screens/Reminders/RescheduleScreen";
import LayoutWrapper from "./Layout/LayoutWrapper";
import SequentialRoute from "./routes/SequentialRoute";
import { StepProvider } from "./context/StepContext";
import KLErrorToaster from "./components/KLErrorToaster";
import {
  AvailabilityRouteStep,
  BusinessRouteStep,
  CustomerRouteStep,
  FlowType,
} from "./constants/routes";
import { UserType } from "./constants/types";

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <RTL>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <StepProvider>
            <Router>
              <KLErrorToaster />
              <Routes>
            {/* Public Routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
            <Route
              path="/terms-of-service"
              element={<TermsOfServiceScreen />}
            />
            <Route path="/not-authorized" element={<NotAuthorizedScreen />} />
            <Route path="/m/:shortCode" element={<ShortLinkRedirectScreen />} />
            <Route
              path="/reminders/confirm/:token"
              element={<ConfirmReminderScreen />}
            />
            <Route
              path="/reminders/cancel/:token"
              element={<CancelReminderScreen />}
            />
            <Route
              path="/reminders/reschedule/:token"
              element={<RescheduleReminderScreen />}
            />

            {/* Layout Wrapper for Protected/Structured Routes */}
            <Route element={<LayoutWrapper />}>
              {/* Main Routes */}
              <Route path="/" element={<MainScreen />} />
              <Route path="/:businessId" element={<CustomerMainScreen />} />

              {/* Customer Flow */}
              <Route
                path={`/:businessId/services`}
                element={<CustomerServicesScreen />}
              />
              {/* SequentialRoute retained on /existing-slots for OME-177's redirect_token handling; deps are [] so it never gates. */}
              <Route
                path={`/:businessId/existing-slots`}
                element={
                  <SequentialRoute
                    routeStep={CustomerRouteStep.ExistingSlots}
                    element={<CustomerExistingSlotsScreen />}
                    sequence={FlowType.Customer}
                  />
                }
              />
              <Route
                path={`/:businessId/slots`}
                element={<CustomerSlotsScreen />}
              />
              <Route
                path={`/:businessId/success`}
                element={<CustomerSuccessScreen />}
              />

              {/* Business Flow */}
              <Route
                path={`/${FlowType.Business}/phone`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.PhoneInput}
                    element={<PhoneInputScreen userType={UserType.Business} />}
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path={`/${FlowType.Business}/verify-otp`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.VerifyOtp}
                    element={
                      <CustomerVerifyOtpScreen userType={UserType.Business} />
                    }
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path={`/${FlowType.Business}/dashboard`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.Dashboard}
                    element={<DashboardScreen />}
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path="/insights/:businessId/:year/:month"
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.Insights}
                    element={<InsightsScreen />}
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path={`/${FlowType.Business}/info`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.Info}
                    element={<OwnerInfoScreen />}
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path={`/${FlowType.Business}/services`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.ServicesSetup}
                    element={<ServicesSetupScreen />}
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path={`/${FlowType.Business}/authorize`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.Authorize}
                    element={<AuthorizeScreen />}
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path={`/${FlowType.Business}/slots`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.SlotsSetup}
                    element={<SlotsSetupScreen isOnboarding />}
                    sequence={FlowType.Business}
                  />
                }
              />
              <Route
                path={`/${FlowType.Business}/success`}
                element={
                  <SequentialRoute
                    routeStep={BusinessRouteStep.Success}
                    element={<OwnerSuccessScreen isOnboarding />}
                    sequence={FlowType.Business}
                  />
                }
              />

              {/* Availability Flow */}
              <Route
                path={`/${FlowType.Availability}/check`}
                element={
                  <SequentialRoute
                    routeStep={AvailabilityRouteStep.Check}
                    element={<AvailabilityScreen />}
                    sequence={FlowType.Availability}
                  />
                }
              />
              <Route
                path={`/${FlowType.Availability}/slots`}
                element={
                  <SequentialRoute
                    routeStep={AvailabilityRouteStep.Slots}
                    element={<SlotsSetupScreen />}
                    sequence={FlowType.Availability}
                  />
                }
              />
              <Route
                path={`/${FlowType.Availability}/success`}
                element={
                  <SequentialRoute
                    routeStep={AvailabilityRouteStep.Success}
                    element={<OwnerSuccessScreen />}
                    sequence={FlowType.Availability}
                  />
                }
              />
            </Route>
          </Routes>
        </Router>
      </StepProvider>
        </ThemeProvider>
      </RTL>
    </Provider>
  );
};

export default App;
